import * as React from 'react';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

// Types
import SimpleArtist from '@common/types/artist/simple-artist';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { ArtistSearchResult } from '@services/artist/types/artist-search-result';

// Services
import { TracInviteTypes } from '@services/invites/types/invite-types';
import { Collaborator } from '@services/collaborator/types/collaborator';
import { NewCollaborator } from '@services/collaborator/types/new-collaborator';

// Utils
import { collaboratorAsArtist } from '@webapp/utils/artists/collaborator-as-artits';

// Store
import { ArtistInviteSource } from '@webapp/store/artist/action-creators';

// Components
import Avatar from '@components/basics/avatar';
import MotionOpacity from '@components/motion/motion-opacity';
import CircleAddIcon from '@components/basics/icons/circle-add';
import CirclePlaceHolder from '@components/basics/circle-placeholder';

// Controllers
import ArtistSearchCtrl from '../artist-search/index';
import ExternalArtistSearch from '../external-artist-search';

interface ArtistInviteSearchCtrlProps {
  id?: string;
  className?: string;
  label: React.ReactNode;
  value?: SimpleArtist | Collaborator | NewCollaborator;
  currentValues: SimpleArtist[] | Collaborator[];
  locked?: boolean;
  decoration?: React.ReactNode;
  hasClose?: boolean;
  firstBigger?: boolean;
  noShadow?: boolean;
  customBorderRadius?: string;
  inHalf?: boolean;
  type: TracInviteTypes;
  role: string;
  songName: string;
  filterRepeated?: boolean;
  fromUser?: boolean;
  releaseId?: string;
  isActiveArtist?: boolean;
  noDropdown?: boolean;
  hasHint?: boolean;
  hasTabsSelector?: boolean;
  isNameOnlyInput?: boolean;
  handleEmailOrPhoneChange?: (newContact: ContactInfo) => void;
  onConfirmName?: () => void;
  contact?: ContactInfo;
  showError: React.ReactNode;
  canBeWithoutInvite?: boolean;
  setShowError?: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  source: ArtistInviteSource;
  dataType: 'artist' | 'collaborator';
  onClose?: () => void;
  onChange: (value: string) => void;
  onSelection: (value: SimpleArtist | ArtistSearchResult) => void;
  setRole?: React.Dispatch<React.SetStateAction<string>>;
  setName?: React.Dispatch<React.SetStateAction<string>>;
  onInvite?: () => void;
  hideEmailInput?: boolean;
}

export const ArtistInviteSearchCtrl: React.FC<ArtistInviteSearchCtrlProps> = ({
  filterRepeated = true,
  value,
  ...props
}) => {
  const [showInvite, setShowInvite] = React.useState(false);
  const [name, setName] = React.useState(value?.name || '');
  const propsSetName = props.setName;

  React.useEffect(
    () => (propsSetName ? propsSetName(name) : undefined),
    [name, propsSetName]
  );

  const id =
    props.dataType === 'collaborator'
      ? (value as Collaborator)?.collaboratorId
      : (value as SimpleArtist)?.id;
  const currentValues: SimpleArtist[] = (props.currentValues as any[]).map(
    (data: SimpleArtist | Collaborator): SimpleArtist => {
      if (props.dataType === 'collaborator') {
        return collaboratorAsArtist(data as Collaborator);
      }
      return data as SimpleArtist;
    }
  );

  React.useEffect(() => {
    value && setName(value?.name || '');
  }, [value]);

  const handleChangeResults = () => {
    setShowInvite(!!name && !id);
  };

  const handleSelection = (artist: SimpleArtist) => {
    if (currentValues.find((a) => a.id === artist.id)) {
      setName('');
      setShowInvite(false);
      return;
    }

    setShowInvite(false);
    setName('');
    props.onSelection(artist);
  };

  const handleExternalSelection = (artist: ArtistSearchResult) => {
    if (currentValues.find((a) => a.id === artist.id)) {
      setName('');
      setShowInvite(false);
      return;
    }

    setShowInvite(false);
    setName('');
    props.onSelection(artist);
  };

  const handleChange = (newValue: string) => {
    if (name.length > newValue.length) {
      setName(newValue);
      props.onChange(newValue);
      return;
    }
    if (newValue.trim() !== '') {
      setName(newValue);
      props.onChange(newValue);
    }
  };
  return (
    <MotionOpacity className={props.className}>
      <Translate>
        {({ translate }) => (
          <Wrapper>
            <div className={props.hasTabsSelector ? 'flex' : ''}>
              {props.type === 'collaborator' ? (
                <ExternalArtistSearch
                  id={props.id}
                  className="artist-search relative"
                  label={props.label}
                  value={
                    props.isActiveArtist
                      ? translate(
                          'RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.INPUT.YOU',
                          { artistName: name }
                        )
                      : name
                  }
                  type={props.type}
                  currentValues={currentValues}
                  locked={props.locked}
                  decoration={props.decoration}
                  leftDecoration={
                    name ? (
                      showInvite ? (
                        <CirclePlaceHolder />
                      ) : (
                        <Avatar size="24" src={value?.avatar} />
                      )
                    ) : (
                      <CircleAddIcon />
                    )
                  }
                  hasClose={props.hasClose}
                  onClose={props.onClose}
                  customBorderRadius={
                    props.hasTabsSelector
                      ? '4px 0 0 4px'
                      : props.hasTabsSelector && (props.hasHint || showInvite)
                      ? '4px 0 0 0'
                      : props.hasHint
                      ? '4px 4px 0 0'
                      : showInvite
                      ? '4px 4px 0 0'
                      : '4px'
                  }
                  noShadow={props.noShadow}
                  parentRelative={true}
                  filterRepeated={filterRepeated}
                  hasTruncate={true}
                  onConfirmName={props.onConfirmName}
                  onChange={handleChange}
                  onSelection={handleExternalSelection}
                  onResultsChange={handleChangeResults}
                />
              ) : (
                <ArtistSearchCtrl
                  id={props.id}
                  className="artist-search relative"
                  label={props.label}
                  value={
                    props.isActiveArtist
                      ? translate(
                          'RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.INPUT.YOU',
                          { artistName: name }
                        )
                      : name
                  }
                  type={props.type}
                  currentValues={currentValues}
                  locked={props.locked}
                  decoration={props.decoration}
                  leftDecoration={
                    name ? (
                      showInvite ? (
                        <CirclePlaceHolder />
                      ) : (
                        <Avatar size="24" src={value?.avatar} />
                      )
                    ) : (
                      <CircleAddIcon />
                    )
                  }
                  hasClose={props.hasClose}
                  onClose={props.onClose}
                  customBorderRadius={
                    props.hasTabsSelector
                      ? '4px 0 0 4px'
                      : props.hasTabsSelector && (props.hasHint || showInvite)
                      ? '4px 0 0 0'
                      : props.hasHint
                      ? '4px 4px 0 0'
                      : showInvite
                      ? '4px 4px 0 0'
                      : '4px'
                  }
                  noShadow={props.noShadow}
                  parentRelative={true}
                  filterRepeated={filterRepeated}
                  hasTruncate={true}
                  onConfirmName={props.onConfirmName}
                  onChange={handleChange}
                  onSelection={handleSelection}
                  onResultsChange={handleChangeResults}
                  isNameOnlyInput={props.type === 'credits'}
                />
              )}
              {props.hasTabsSelector ? props.children : null}
            </div>
            {!props.hasTabsSelector ? props.children : null}
          </Wrapper>
        )}
      </Translate>
    </MotionOpacity>
  );
};

export default styled(ArtistInviteSearchCtrl)`
  .searchbar-wrapper {
    flex-grow: 1;
  }

  .flex {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 3px;
    position: relative;
  }

  .invite-input {
    width: 100%;
    margin-bottom: 10px;
  }

  .relative {
    position: relative;
  }
`;

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3px;
`;
